import { useEffect, useState } from "react";
import { useEvent } from "@/context/EventContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Trash2, Crown, Clock } from "lucide-react";

export function CohostManager() {
  const { eapi } = useEvent();
  const [list, setList] = useState([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => eapi.get("/cohosts").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const invite = async () => {
    if (!email.trim()) { toast.error("Enter an email address"); return; }
    setBusy(true);
    try {
      const { data } = await eapi.post("/cohosts", { email: email.trim(), base_url: window.location.origin });
      toast.success(data.status === "accepted" ? "Co-host added" : "Invitation sent");
      setEmail(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await eapi.delete(`/cohosts/${id}`); toast.success("Co-host removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <section className="bg-white border border-[#E8E6E1] rounded-lg p-6 mb-6" data-testid="cohost-section">
      <p className="overline">Co-hosts</p>
      <p className="text-xs text-stone-400 mt-1 mb-4">Invite a partner or planner to co-host. Co-hosts get full access and share any unlocked features. Only you, the primary host, can add or remove co-hosts.</p>
      <div className="flex gap-2 mb-4">
        <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="cohost@email.com" data-testid="cohost-email"
          className="flex-1 rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86]" />
        <button onClick={invite} disabled={busy} data-testid="cohost-invite-btn"
          className="flex items-center gap-2 rounded-full bg-[#2C2A28] text-white px-5 py-2.5 text-sm hover:bg-[#8C9A86] transition-colors disabled:opacity-50">
          <UserPlus size={15} /> {busy ? "Sending…" : "Invite"}
        </button>
      </div>
      {list.length === 0
        ? <p className="text-sm text-stone-400">No co-hosts yet.</p>
        : <div className="space-y-2">
            {list.map((c) => (
              <div key={c.id} data-testid={`cohost-row-${c.id}`} className="flex items-center gap-3 px-4 py-3 rounded-md border border-[#F0EEE9]">
                <div className="w-8 h-8 rounded-full bg-[#8C9A86]/12 grid place-items-center text-[#8C9A86]">{c.status === "accepted" ? <Crown size={15} /> : <Clock size={15} />}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-800 truncate">{c.name || c.email}</p>
                  <p className="text-xs text-stone-400 truncate">{c.name ? `${c.email} · ` : ""}{c.status === "pending" ? "invitation pending" : "co-host"}</p>
                </div>
                <button onClick={() => remove(c.id)} data-testid={`cohost-remove-${c.id}`} className="text-stone-400 hover:text-[#C67A7A]"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>}
    </section>
  );
}
